<template>
  <div>
    <b-modal
      v-model="showModal"
      title="🎂 Aniversariantes do Mês"
      size="md"
      centered
      ok-only
      ok-title="Fechar"
      ok-variant="primary"
      header-class="birthdays-modal-header"
      body-class="birthdays-modal-body"
      footer-class="birthdays-modal-footer"
      @hidden="onClose"
    >
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" small />
        <p class="mt-2 text-muted">Carregando aniversariantes...</p>
      </div>

      <div v-else-if="aniversariantes.length === 0" class="text-center py-4">
        <calendar-icon size="32" class="text-muted mb-2" />
        <p class="text-muted mb-0">Nenhum aniversariante neste mês</p>
      </div>

      <div v-else>
        <p class="mb-3 birthday-count">
          <gift-icon size="16" class="mr-1" />
          {{ aniversariantes.length }} aniversariante{{ aniversariantes.length > 1 ? 's' : '' }} neste mês
        </p>
        <div
          v-for="(item, idx) in aniversariantes"
          :key="idx"
          class="birthday-item"
          @click="goToCliente(item.clienteid)"
        >
          <div class="birthday-avatar">
            {{ getInitials(item.nome) }}
            <span class="birthday-day">{{ item.dia }}</span>
          </div>
          <div class="birthday-info">
            <span class="birthday-name">{{ item.nome }}</span>
            <span v-if="item.cargo" class="birthday-cargo">{{ item.cargo }}</span>
            <span class="birthday-client">{{ item.cliente_fantasia || item.cliente_razao }}</span>
          </div>
          <div class="birthday-actions">
            <!-- Send Card Action (Disabled if already sent) -->
            <button
              v-if="item.email"
              :class="['birthday-action', isCardSent(item) ? 'btn-sent' : 'btn-send-card']"
              :disabled="isCardSent(item)"
              @click.stop="openCardPreview(item)"
              :title="isCardSent(item) ? 'Cartão Enviado' : 'Enviar Cartão Premium'"
            >
              <check-icon v-if="isCardSent(item)" size="14" />
              <gift-icon v-else size="14" />
            </button>
            <a v-if="item.telefone || item.celular" :href="'tel:' + (item.celular || item.telefone)" class="birthday-action" @click.stop title="Ligar">
              <phone-icon size="14" />
            </a>
            <a v-if="item.email" :href="'mailto:' + item.email" class="birthday-action" @click.stop title="E-mail Padrão">
              <mail-icon size="14" />
            </a>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Card Preview Modal Component -->
    <cartao-aniversario
      :visible="showCardModal"
      :contact="selectedContact"
      @update:visible="showCardModal = $event"
      @sent="onCardSent"
    />
  </div>
</template>

<script>
import { CalendarIcon, GiftIcon, PhoneIcon, MailIcon, CheckIcon } from 'vue-feather-icons';
import crmClient from '@/api/CRM/crm.client';
import CartaoAniversario from '@/components/CartaoAniversario.vue';
import { PERFIS_CLIENTE } from '@/libs/perfilCliente';

export default {
  name: 'BirthdaysModal',
  components: { 
    CalendarIcon, GiftIcon, PhoneIcon, MailIcon, CheckIcon, CartaoAniversario 
  },
  data() {
    return {
      showModal: false,
      showCardModal: false,
      loading: false,
      aniversariantes: [],
      selectedContact: null,
      sentRecords: {}, // cache of what was sent to reactively disable buttons
    };
  },
  mounted() {
    this.checkBirthdays();
    this.$root.$on('open-birthdays-modal', () => {
      this.forceCheckBirthdays();
    });
  },
  destroyed() {
    this.$root.$off('open-birthdays-modal');
  },
  methods: {
    async forceCheckBirthdays() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
        const perfilNome = perfilLogado.perfil_acesso_nome || '';
        if (!perfilNome || PERFIS_CLIENTE.includes(perfilNome)) return;

        this.loading = true;
        this.showModal = true;
        
        const currentMonth = new Date().getMonth() + 1;
        const response = await crmClient.aniversariosMes(currentMonth);
        this.aniversariantes = response.data || [];
        
        this.cacheSentStatus();
      } catch (error) {
        console.error('Erro ao buscar aniversários:', error);
      } finally {
        this.loading = false;
      }
    },
    async checkBirthdays() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
        const perfilNome = perfilLogado.perfil_acesso_nome || '';
        if (!perfilNome || PERFIS_CLIENTE.includes(perfilNome)) return;

        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const currentDay = today.getDate();

        this.loading = true;

        const response = await crmClient.aniversariosMes(currentMonth);
        this.aniversariantes = response.data || [];

        const temAniversarioHoje = this.aniversariantes.some(
          item => parseInt(item.dia, 10) === currentDay
        );

        if (temAniversarioHoje) {
          this.showModal = true;
        }

        this.cacheSentStatus();

        localStorage.setItem('crm_birthday_last_check', today.toISOString().slice(0, 10));
      } catch (error) {
        console.error('Erro ao verificar aniversários:', error);
      } finally {
        this.loading = false;
      }
    },
    
    cacheSentStatus() {
      const year = new Date().getFullYear();
      const records = {};
      this.aniversariantes.forEach(item => {
        const key = `birthday_sent_${item.clienteiderp || item.email}_${year}`;
        if (localStorage.getItem(key)) {
          records[key] = true;
        }
      });
      this.sentRecords = { ...records };
    },

    isCardSent(item) {
      const year = new Date().getFullYear();
      const key = `birthday_sent_${item.clienteiderp || item.email}_${year}`;
      return !!this.sentRecords[key];
    },

    openCardPreview(contact) {
      this.selectedContact = contact;
      this.showCardModal = true;
    },

    onCardSent(contact) {
       const year = new Date().getFullYear();
       const key = `birthday_sent_${contact.clienteiderp || contact.email}_${year}`;
       this.$set(this.sentRecords, key, true);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },
    
    goToCliente(clienteId) {
      if (!clienteId) return;
      this.showModal = false;
      this.$router.push({ name: 'crm-cliente-detail', params: { id: clienteId } }).catch(() => {});
    },
    
    onClose() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
/* Modal Headers and Body */
.birthdays-modal-header {
  background: var(--card-bg, #fff);
  border-bottom: 1px solid var(--border-color, #e9ecef);
}

.birthdays-modal-body {
  background: var(--card-bg, #fff);
}

.birthdays-modal-footer {
  background: var(--card-bg, #fff);
  border-top: 1px solid var(--border-color, #e9ecef);
}

/* Birthday Count Text */
.birthday-count {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-heading, #333);
  display: flex;
  align-items: center;
}

/* Birthday Item Container */
.birthday-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: background 0.15s;
  background: var(--subtle-bg, #f8f9fa);
  border: 1px solid transparent;
}

.birthday-item:hover {
  background: var(--border-color, #e9ecef);
  border-color: rgba(99, 102, 241, 0.2);
}

/* Avatar Circle */
.birthday-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #01094e;
  background: linear-gradient(135deg, #01094e, #0575b5);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 4px 10px rgba(1, 9, 78, 0.2);
}

.birthday-day {
  font-size: 0.55rem;
  opacity: 0.8;
  margin-top: -2px;
}

/* Birthday Info Section */
.birthday-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.birthday-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-heading, #1e293b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.birthday-cargo {
  font-size: 0.75rem;
  color: var(--text-muted, #64748b);
}

.birthday-client {
  font-size: 0.75rem;
  color: var(--primary, #0575b5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

/* Action Buttons */
.birthday-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.birthday-action {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted, #6c757d);
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  transition: all 0.2s;
  text-decoration: none;
  cursor: pointer;
}

.birthday-action:hover:not(:disabled) {
  color: #0575b5;
  border-color: #0575b5;
  background: rgba(5, 117, 181, 0.05);
}

.btn-send-card {
  color: #c87941;
  border-color: #c87941;
  background: rgba(200, 121, 65, 0.05);
}

.btn-send-card:hover:not(:disabled) {
  background: linear-gradient(135deg, #c87941, #f2c88a);
  color: #040b15;
  border-color: transparent;
  box-shadow: 0 4px 10px rgba(200, 121, 65, 0.2);
}

.btn-sent {
  background: #f1f5f9;
  color: #10b981;
  border-color: #10b981;
  cursor: not-allowed;
  opacity: 0.8;
}

/* Dark Mode Styles */
body.dark-layout .birthdays-modal-header {
  background: #1e293b;
  border-bottom-color: #334155;
}

body.dark-layout .birthdays-modal-body {
  background: #1e293b;
}

body.dark-layout .birthdays-modal-footer {
  background: #1e293b;
  border-top-color: #334155;
}

body.dark-layout .birthday-count {
  color: #e2e8f0;
}

body.dark-layout .birthday-item {
  background: #273447;
  border-color: #334155;
}

body.dark-layout .birthday-item:hover {
  background: #334155;
  border-color: #475569;
}

body.dark-layout .birthday-name {
  color: #e2e8f0;
}

body.dark-layout .birthday-cargo {
  color: #cbd5e1;
}

body.dark-layout .birthday-client {
  color: #54b3d9;
}

body.dark-layout .birthday-action {
  background: #273447;
  border-color: #475569;
  color: #94a3b8;
}

body.dark-layout .birthday-action:hover:not(:disabled) {
  color: #54b3d9;
  border-color: #54b3d9;
  background: rgba(84, 179, 217, 0.1);
}

body.dark-layout .btn-send-card {
  color: #f2c88a;
  border-color: #f2c88a;
  background: rgba(242, 200, 138, 0.1);
}

body.dark-layout .btn-send-card:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(242, 200, 138, 0.2), rgba(200, 121, 65, 0.2));
  color: #f2c88a;
  border-color: #f2c88a;
  box-shadow: 0 4px 10px rgba(200, 121, 65, 0.3);
}

body.dark-layout .btn-sent {
  background: rgba(16, 185, 129, 0.1);
  color: #6ee7b7;
  border-color: #6ee7b7;
}
</style>
